import { useState } from "react";
import { timeline } from "@/mocks/home";

const tones: Record<string, string> = {
  平静: { chip: "bg-stone-100 text-stone-600", dot: "bg-stone-400" },
  高热: { chip: "bg-red-50 text-red-600", dot: "bg-red-500" },
  混乱: { chip: "bg-amber-50 text-amber-600", dot: "bg-amber-500" },
  降温: { chip: "bg-orange-50 text-orange-600", dot: "bg-orange-400" },
  沉淀: { chip: "bg-[#f3e9da] text-[#a35f35]", dot: "bg-[#c2410c]" },
};

export default function Timeline() {
  const allTones = [...new Set(timeline.stages.map((s) => s.tone))];
  const [toneFilter, setToneFilter] = useState("全部");

  const shown = timeline.stages.filter(
    (s) => toneFilter === "全部" || s.tone === toneFilter
  );

  return (
    <section id="full" className="scroll-mt-24 bg-[#f4ecdf]">
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-10 md:py-14">
        <div className="max-w-3xl">
          <div className="text-xs font-semibold tracking-widest text-[#c2410c] mb-2 flex items-center gap-2">
            <i className="ri-history-line"></i> {timeline.kick}
          </div>
          <h2 className="font-display text-2xl md:text-4xl font-bold text-[#241c15] leading-tight">
            {timeline.title}
          </h2>
          <p className="mt-4 text-[15px] leading-7 text-[#5b4f45]">{timeline.subtitle}</p>
        </div>

        <div className="mt-8 flex flex-wrap gap-2">
          <button
            onClick={() => setToneFilter("全部")}
            className={`text-xs rounded-full px-3.5 py-1.5 whitespace-nowrap transition-colors cursor-pointer ${
              toneFilter === "全部"
                ? "bg-[#c2410c] text-white font-semibold"
                : "bg-white border border-[#e0c9ae] text-[#6b5d4d] hover:border-[#c2410c]"
            }`}
          >
            全部阶段
          </button>
          {allTones.map((t) => (
            <button
              key={t}
              onClick={() => setToneFilter(t)}
              className={`text-xs rounded-full px-3.5 py-1.5 whitespace-nowrap transition-colors cursor-pointer ${
                toneFilter === t
                  ? "bg-[#c2410c] text-white font-semibold"
                  : "bg-white border border-[#e0c9ae] text-[#6b5d4d] hover:border-[#c2410c]"
              }`}
            >
              <i className={`${tones[t].dot} inline-block w-2 h-2 rounded-full mr-1.5`}></i>
              {t}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-8">
          {/* timeline list */}
          <div className="lg:col-span-2">
            <div className="relative border-l-2 border-[#d8c2a6] pl-6 md:pl-8 space-y-8">
              {shown.map((s, idx) => {
                const tone = tones[s.tone] || tones["平静"];
                return (
                  <article key={s.phase} className="relative">
                    <span
                      className={`absolute -left-[35px] md:-left-[43px] top-2 w-4 h-4 rounded-full border-2 border-[#f4ecdf] ${tone.dot}`}
                    ></span>
                    <div className="bg-white rounded-xl border border-[#e5d2ba] p-5 hover:border-[#c9a576] transition-colors">
                      <div className="flex items-center gap-3 flex-wrap">
                        <h3 className="font-display font-bold text-lg text-[#241c15]">
                          {s.phase}
                        </h3>
                        <span className="text-xs text-[#9a8772] inline-flex items-center gap-1">
                          <i className="ri-calendar-line"></i>
                          {s.date}
                        </span>
                        <span
                          className={`text-[11px] rounded-full px-2.5 py-0.5 ml-auto ${tone.chip} whitespace-nowrap`}
                        >
                          {s.tone}
                        </span>
                      </div>
                      <ul className="mt-3 space-y-2">
                        {s.points.map((p, i) => (
                          <li key={i} className="flex gap-2.5 text-[14px] leading-6 text-[#5b4f45]">
                            <i className="ri-checkbox-circle-line text-[#c2410c] mt-1 shrink-0"></i>
                            <span>{p}</span>
                          </li>
                        ))}
                      </ul>
                      <div className="mt-3 text-[10px] text-[#a08b73] flex items-center gap-1.5 border-t border-dashed border-[#ead9c5] pt-2">
                        <i className="ri-folder-check-line"></i>
                        本阶段信源：公开公告 / 平台规则 / 可验证记录
                      </div>
                    </div>
                  </article>
                );
              })}
              {shown.length === 0 && (
                <p className="text-sm text-[#9a8772]">该情绪阶段暂无独立条目，切换到「全部阶段」查看。</p>
              )}
            </div>

            <div className="mt-8 bg-[#201a14] text-[#f4e9dc] rounded-xl p-5 md:p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-start gap-3">
                <i className="ri-live-line text-orange-400 text-2xl mt-0.5"></i>
                <div>
                  <h4 className="text-sm font-semibold text-[#ffe9d6]">本篇仍在对「{timeline.eventName}」持续跟进</h4>
                  <p className="text-xs text-[#b6a38c] mt-1">
                    出现新公告、更正或关键证据时，我们将实时追加到时间线末尾。
                  </p>
                </div>
              </div>
              <a
                href="#about"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#201a14] bg-orange-400 rounded-md px-4 py-2.5 whitespace-nowrap hover:bg-orange-300 transition-colors cursor-pointer"
              >
                <i className="ri-notification-4-line"></i>
                订阅后续进展
              </a>
            </div>
          </div>

          {/* aside stats */}
          <aside className="space-y-5 h-fit lg:sticky lg:top-24">
            <div className="bg-white rounded-xl border border-[#e5d2ba] p-5">
              <h3 className="text-sm font-semibold text-[#241c15] mb-4 flex items-center gap-2">
                <i className="ri-bar-chart-box-line text-[#c2410c]"></i>
                跟踪数据
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {timeline.stageList.map((s) => (
                  <div
                    key={s.name}
                    className="bg-[#faf5ee] rounded-lg p-3 text-center border border-[#f0e0ca]"
                  >
                    <div className="font-number text-2xl leading-none text-[#c2410c]">{s.value}</div>
                    <div className="text-[12px] font-medium text-[#3a2f24] mt-1.5">{s.name}</div>
                    <div className="text-[10px] text-[#9a8772] mt-0.5">{s.desc}</div>
                  </div>
                ))}
              </div>
              <p className="text-[10px] leading-5 text-[#a08b73] mt-3 border-t border-[#f0e5d6] pt-2">
                数据为编辑部复核口径，不含未证实传言；比例随信息补强动态更新。
              </p>
            </div>

            <div className="bg-[#fff4e6] border border-[#eccfa8] rounded-xl p-5">
              <div className="flex items-center gap-2 text-[#a35f35]">
                <i className="ri-lightbulb-flash-line text-xl"></i>
                <h3 className="text-sm font-semibold">为什么要按阶段看？</h3>
              </div>
              <p className="text-[13px] leading-6 text-[#7a6a58] mt-2">
                热点往往不是一次性爆发，而是「信号 → 引爆 → 分裂 → 澄清 → 沉淀」的循环。把每个节点还原到时间轴上，才能看清哪些信息先出现、哪些后澄清。
              </p>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}